import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Mail } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import {
  urlCourrierAvertissement,
  useGenererAvertissement,
  useInformerEntreprise,
  useJustifierAbsence,
  type DemiJourneeAbsente,
  type LigneJournee,
  type MotifJustification,
} from '../../lib/useAbsences';
import { useToast } from '../ui/useToast';
import { ChoixMotif, motifComplet } from './ChoixMotif';

function dateLisible(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * Détail d'une journée d'absence : chaque demi-journée avec sa justification
 * et l'information de l'employeur, le cumul du dossier et son courrier. Les
 * gestes et leurs refus viennent de l'API.
 */
export function ModaleDetailAbsence({
  ligne,
  motifs,
  ouvrirDossier,
  onFermer,
}: {
  ligne: LigneJournee | null;
  motifs: MotifJustification[];
  /** Le compte ouvre-t-il le dossier apprenant (`adm-dossier`) ? */
  ouvrirDossier: boolean;
  onFermer: () => void;
}): JSX.Element | null {
  const toast = useToast();
  const avertir = useGenererAvertissement();
  if (!ligne) return null;

  const { cumul, avertissement } = ligne;

  return (
    <Modale
      titre={`${ligne.apprenant} · ${ligne.jourLisible}`}
      sousTitre={`${ligne.typeLibelle} · ${ligne.formation ?? 'Formation non précisée'} · ${ligne.siteCode}`}
      ouverte
      onFermer={onFermer}
    >
      <div className="space-y-5 p-4 sm:p-6">
        {ligne.demiJournees.map((demi) => (
          <DemiJournee key={demi.presenceId} demi={demi} motifs={motifs} />
        ))}

        <section className="space-y-2 rounded-xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">Cumul du dossier</span>
            <span
              className={`font-mono text-sm font-bold ${
                cumul.seuilDepasse ? 'text-red-600' : cumul.margeAvantSeuil <= 1 ? 'text-amber-600' : 'text-codialis-teal'
              }`}
            >
              {cumul.injustifiees} / {cumul.seuil}
            </span>
          </div>
          <p className="text-xs text-codialis-gray">
            Demi-journées non justifiées sur des feuilles clôturées, depuis le début du parcours
            {cumul.justifiees > 0 ? ` · ${cumul.justifiees} justifiée(s), hors décompte` : ''}.
          </p>
          {avertissement.dernier && (
            <p className="flex flex-wrap items-center gap-2 text-xs">
              <FileText className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
              Courrier n°{avertissement.dernier.palier} transmis le {dateLisible(avertissement.dernier.le)}
              {avertissement.dernier.documentId && (
                <a
                  href={urlCourrierAvertissement(avertissement.dernier.documentId)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center [@media(pointer:coarse)]:min-h-[40px] font-semibold text-codialis-sky hover:underline"
                >
                  Lire le courrier
                </a>
              )}
            </p>
          )}
          {avertissement.palierDu !== null && (
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <span className="text-xs text-red-700">
                Palier {avertissement.palierDu} atteint : le courrier n’est pas encore parti.
              </span>
              <BoutonPrincipal
                disabled={avertir.isPending}
                onClick={() =>
                  avertir.mutate(ligne.apprenantId, { onSuccess: (reponse) => toast(reponse.message) })
                }
              >
                Générer et transmettre le courrier
              </BoutonPrincipal>
            </div>
          )}
          {avertir.error instanceof ErreurApi && <MessageErreur message={avertir.error.message} />}
        </section>

        {ouvrirDossier && (
          <Link
            to={`/adm-dossier/${ligne.apprenantId}`}
            className="inline-flex items-center [@media(pointer:coarse)]:min-h-[40px] text-xs font-bold text-codialis-sky hover:underline"
          >
            Ouvrir le dossier →
          </Link>
        )}
      </div>
    </Modale>
  );
}

function DemiJournee({
  demi,
  motifs,
}: {
  demi: DemiJourneeAbsente;
  motifs: MotifJustification[];
}): JSX.Element {
  const toast = useToast();
  const justifier = useJustifierAbsence();
  const informer = useInformerEntreprise();
  const [saisie, setSaisie] = useState(false);
  const [motif, setMotif] = useState('');
  const [precision, setPrecision] = useState('');
  const erreur = justifier.error ?? informer.error;

  return (
    <section className="space-y-3 rounded-xl border border-gray-100 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-codialis-navy">
            {demi.demiJourneeLibelle} · {demi.horaires}
          </div>
          <div className="text-xs text-codialis-gray">{demi.seance}</div>
        </div>
        {demi.provisoire ? (
          <Pastille texte="Feuille ouverte · provisoire" ton="info" />
        ) : demi.justifiee ? (
          <Pastille texte="Justifiée" ton="success" />
        ) : demi.justificatifEnAttente ? (
          <Pastille texte="Justificatif à examiner" ton="info" />
        ) : (
          <Pastille texte="Sans justificatif" ton="danger" />
        )}
      </div>

      {demi.justifiee && demi.motifJustification && (
        <p className="text-xs text-codialis-navy">{demi.motifJustification}</p>
      )}

      {!demi.provisoire && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-codialis-gray">
          <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
          <Pastille texte={demi.informationLibelle} ton={demi.informationTon} />
          {demi.entrepriseNotifieeA && <span className="min-w-0 break-all">{demi.entrepriseNotifieeA}</span>}
        </div>
      )}
      {demi.refusInformation && demi.information !== 'ENVOYEE' && (
        <p className="text-xs text-codialis-gray">{demi.refusInformation}</p>
      )}

      {saisie && (
        <div className="space-y-3 rounded-xl bg-gray-50 p-3">
          <ChoixMotif
            motifs={motifs}
            motif={motif}
            precision={precision}
            onMotif={setMotif}
            onPrecision={setPrecision}
            prefixe={`justif-${demi.presenceId}`}
          />
          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal
              disabled={!motifComplet(motifs, motif, precision) || justifier.isPending}
              onClick={() =>
                justifier.mutate(
                  {
                    presenceId: demi.presenceId,
                    justifiee: true,
                    motif,
                    ...(precision.trim() ? { precision: precision.trim() } : {}),
                  },
                  {
                    onSuccess: (reponse) => {
                      toast(reponse.message);
                      setSaisie(false);
                    },
                  },
                )
              }
            >
              Enregistrer la justification
            </BoutonPrincipal>
            <BoutonSecondaire onClick={() => setSaisie(false)}>Annuler</BoutonSecondaire>
          </div>
        </div>
      )}

      {erreur instanceof ErreurApi && <MessageErreur message={erreur.message} />}

      <div className="flex flex-wrap gap-2">
        {!saisie &&
          (demi.justifiee ? (
            <BoutonSecondaire
              disabled={justifier.isPending}
              onClick={() =>
                justifier.mutate(
                  { presenceId: demi.presenceId, justifiee: false },
                  { onSuccess: (reponse) => toast(reponse.message) },
                )
              }
            >
              Retirer la justification
            </BoutonSecondaire>
          ) : (
            <BoutonSecondaire onClick={() => setSaisie(true)}>Justifier</BoutonSecondaire>
          ))}
        {demi.refusInformation === null && (
          <BoutonSecondaire
            disabled={informer.isPending}
            onClick={() =>
              informer.mutate(demi.presenceId, { onSuccess: (reponse) => toast(reponse.message) })
            }
          >
            {demi.information === 'ENVOYEE' ? 'Renvoyer le mail' : 'Envoyer le mail'}
          </BoutonSecondaire>
        )}
      </div>
    </section>
  );
}
